import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { ServiceCard, CTABanner, Button } from '../components/ui/UI';
import { SERVICES, CATEGORIES } from '../data/services';
import './Services.css';

export default function Services() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery]       = useState('');
  const navigate = useNavigate();

  const handleInquire = (serviceTitle) => {
    navigate('/contact', { state: { service: serviceTitle } });
  };

  const filtered = SERVICES.filter(s => {
    const matchCat  = activeCategory === 'all' || s.cat === activeCategory;
    const matchSearch = searchQuery === '' ||
      s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.short.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="page-wrapper services-page">
      {/* ── ENHANCED SERVICES HERO ─────────────────────────── */}
      <section className="services-hero">
        <div className="services-hero__bg" />
        <div className="container services-hero__inner">
          <div className="services-hero__grid">
            <div className="services-hero__content animate-fade-up">
              <span className="section-badge badge-white">Service Catalog</span>
              <h1 className="services-hero__title">
                57+ Premium Services<br />
                <span className="services-hero__highlight">For Every Need</span>
              </h1>
              <p className="services-hero__sub">
                Browse our complete range of services - from luxury private jets and 5-star hotel bookings to education, jobs, and city tours. Everything you need in Mumbai, all in one place.
              </p>
              <div className="services-hero__features">
                <div className="services-feat-item">
                  <span className="services-feat-icon">⚡</span>
                  <span>Instant Response</span>
                </div>
                <div className="services-feat-item">
                  <span className="services-feat-icon">🌐</span>
                  <span>Global Standards</span>
                </div>
                <div className="services-feat-item">
                  <span className="services-feat-icon">🛡️</span>
                  <span>100% Verified</span>
                </div>
              </div>
            </div>

            <div className="services-hero__visual animate-fade-in delay-2">
              <div className="services-visual-card services-visual-card--1">
                <div className="services-visual-card__img">💼</div>
                <div className="services-visual-card__text">
                  <strong>Business</strong>
                  <span>Tech & Sourcing</span>
                </div>
              </div>
              <div className="services-visual-card services-visual-card--2">
                <div className="services-visual-card__img">🛫</div>
                <div className="services-visual-card__text">
                  <strong>Airport Jobs</strong>
                  <span>Global Placements</span>
                </div>
              </div>
              <div className="services-visual-card services-visual-card--3">
                <div className="services-visual-card__img">🏛️</div>
                <div className="services-visual-card__text">
                  <strong>Admissions</strong>
                  <span>Top Colleges</span>
                </div>
              </div>
              <div className="services-visual-card services-visual-card--4">
                <div className="services-visual-card__img">🛵</div>
                <div className="services-visual-card__text">
                  <strong>Rentals</strong>
                  <span>Cars & Bikes</span>
                </div>
              </div>
              <div className="services-hero__deco-circle" />
            </div>
          </div>
        </div>
      </section>

      {/* Filter & Search bar */}
      <div className="services-filter-bar">
        <div className="container services-filter-bar__inner">
          {/* Search */}
          <div className="services-search">
            <Search size={16} className="services-search__icon" />
            <input
              type="text"
              placeholder="Search services..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="services-search__input"
            />
          </div>

          {/* Category filters */}
          <div className="services-filters">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                className={`services-filter-btn services-filter-btn--${cat.color} ${activeCategory === cat.id ? 'services-filter-btn--active' : ''}`}
                onClick={() => { setActiveCategory(cat.id); setSearchQuery(''); }}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      <section className="section-pad">
        <div className="container">
          <div className="services-results-header">
            <p className="services-results-count">
              Showing <strong>{filtered.length}</strong> service{filtered.length !== 1 ? 's' : ''}
              {activeCategory !== 'all' && ` in "${CATEGORIES.find(c => c.id === activeCategory)?.label}"`}
              {searchQuery && ` matching "${searchQuery}"`}
            </p>
            {(activeCategory !== 'all' || searchQuery) && (
              <button
                className="services-clear-btn"
                onClick={() => { setActiveCategory('all'); setSearchQuery(''); }}
              >
                Clear filters ×
              </button>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="services-empty">
              <div className="services-empty__icon">🔍</div>
              <h3>No services found</h3>
              <p>Try a different search term or category.</p>
              <button
                className="services-clear-btn"
                onClick={() => { setActiveCategory('all'); setSearchQuery(''); }}
              >
                Show all services
              </button>
            </div>
          ) : (
            <div className="services-grid">
              {filtered.map(s => (
                <ServiceCard key={s.id} service={s} onInquire={handleInquire} />
              ))}
            </div>
          )}
        </div>
      </section>

      <CTABanner
        title="Can't find what you're looking for?"
        sub="Contact us directly and we'll connect you to the right service - no matter what you need."
      />
    </div>
  );
}
